import { FC, Suspense } from 'react';

import {
  Link,
  Outlet,
  useLoaderData,
  useRouteLoaderData,
} from 'react-router-dom';
import {
  graphql,
  loadQuery,
  PreloadedQuery,
  usePreloadedQuery,
} from 'react-relay';
import { IEnvironment } from 'relay-runtime';
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

import {
  DynamicBreadcrumb,
  EdButtonLink,
  EdTextLink,
  IconJustify,
} from 'shared-ui';

import {
  IdentityUserLayoutQuery,
  IdentityUserLayoutQuery$data,
} from './__generated__/IdentityUserLayoutQuery.graphql';

export const routeId = 'identity-user-home';

const queryDef = graphql`
  query IdentityUserLayoutQuery($identityUserCode: Long!) {
    identityUser: identityUserByCode(code: $identityUserCode) {
      __id
      id
      code
      identityUserId
      lastLoggedIn
      updatedAt
      createdAt
      firstName
      lastName
      email
      accountTypes
    }
  }
`;

type Props = {
  showHeader: boolean;
  inList: boolean;
};

export const IdentityUserLayout: FC<Props> = ({ inList, showHeader }) => {
  const preloadedQuery =
    useLoaderData() as PreloadedQuery<IdentityUserLayoutQuery>;
  const data = usePreloadedQuery(queryDef, preloadedQuery);

  const { identityUser } = data;

  if (identityUser === null || identityUser === undefined) {
    return <div>Not Found</div>;
  }

  return (
    <Suspense fallback={'Loading...'}>
      <div className='flex flex-1 flex-col'>
        {showHeader && (
          <div>
            <div className='flex flex-wrap items-center justify-between border-b'>
              <DynamicBreadcrumb />
              <div className='text-primary-500 px-2 font-medium uppercase sm:px-8'>
                {identityUser.email}
              </div>
            </div>
          </div>
        )}

        <div className='flex flex-wrap items-center justify-between border-b p-2'>
          <div className='flex flex-wrap items-center gap-2 border-b bg-gray-50 px-4'>
            <EdTextLink
              id='identity-user-root-link'
              to='details'
              label='Details'
            />
          </div>

          {inList && (
            <div>
              <EdButtonLink
                to={`../../../../${identityUser.code}`}
                name='View'
                icon={ArrowsPointingOutIcon}
                iconJustify={IconJustify.Right}
              />
            </div>
          )}
        </div>

        <Outlet />
      </div>
    </Suspense>
  );
};

type LinkProps = {
  routeId: string;
  label?: string;
};
export const IdentityUserGroupLink: FC<LinkProps> = ({ routeId, label }) => {
  const data = useIdentityUserRouteData(routeId);

  if (!data.identityUser) {
    return <></>;
  }

  const name = `${data.identityUser.email}`;

  return (
    <Suspense fallback={'Loading...'}>
      <Link to='.'>{label || name}</Link>
    </Suspense>
  );
};

export async function loader(environment: IEnvironment, params: any) {
  const code = params.identityUserCode as string;
  const identityUserCode = parseInt(code);
  return loadQuery<IdentityUserLayoutQuery>(environment, queryDef, {
    identityUserCode,
  });
}

export function useIdentityUserRouteData(
  routeId: string,
): IdentityUserLayoutQuery$data {
  const queryRef = useRouteLoaderData(
    routeId,
  ) as PreloadedQuery<IdentityUserLayoutQuery>;
  const data = usePreloadedQuery<IdentityUserLayoutQuery>(queryDef, queryRef);
  return data;
}
